import type { GuideLessonSummary } from "../../../../../api/kehrblatt/learning-guide"

const milestones = [
  {
    key: "erste-sammelplanung",
    title: "Die erste Sammelplanung verstehen",
    lessonKeys: [
      "navigation",
      "tabellen",
      "liegenschaften-kontakte",
      "liste-exportieren",
      "auftraege-vorbereiten",
      "termine-planen",
      "kunden-informieren",
    ],
  },
  {
    key: "alltag",
    title: "Aufträge und Termine im Alltag bearbeiten",
    lessonKeys: ["auftraege", "termine-verschieben", "kalender-abwesenheiten"],
  },
  {
    key: "rueckmeldungen",
    title: "Rückmeldungen und Briefe bearbeiten",
    lessonKeys: ["mitteilungen", "kontaktdaten", "terminrueckmeldungen", "terminbriefe"],
  },
  {
    key: "einrichtung",
    title: "Den Betrieb passend einrichten",
    lessonKeys: [
      "import",
      "mitarbeiter",
      "betriebsdaten",
      "planung-vorlagen",
      "kalender-verbinden",
      "telefon-weiterleitung",
      "assistent",
    ],
  },
] as const

const orderedLessonKeys: readonly string[] = milestones.flatMap((milestone) => [
  ...milestone.lessonKeys,
])

/** Display numbering follows the guided path; stored catalog positions and progress stay unchanged. */
export function getLearningLessonNumber(lessonKey: string): number | null {
  const index = orderedLessonKeys.indexOf(lessonKey)

  return index < 0 ? null : index + 1
}

export const isLearningSetupPath = (search: string) =>
  new URLSearchParams(search).get("lernweg") === "einrichtung"

/** Only the learning path survives chapter changes; stage and branch choices belong to a chapter. */
export const getLearningOverviewHref = (basePath: string, search = "") =>
  `${basePath}${isLearningSetupPath(search) ? "?lernweg=einrichtung" : ""}`

export const getLearningLessonHref = (
  basePath: string,
  lesson: Pick<GuideLessonSummary, "key" | "topic_key">,
  search = "",
) =>
  getLearningOverviewHref(
    `${basePath}/${encodeURIComponent(lesson.topic_key)}/${encodeURIComponent(lesson.key)}`,
    search,
  )

export function getLearningPathLessons(lessons: GuideLessonSummary[], search = "") {
  const keys = isLearningSetupPath(search) ? milestones[3].lessonKeys : orderedLessonKeys
  const byKey = new Map(lessons.map((lesson) => [lesson.key, lesson]))

  return keys.flatMap((key) => {
    const lesson = byKey.get(key)

    return lesson ? [lesson] : []
  })
}

/** The next chapter follows the selected learning path, independent of catalog display positions. */
export function getNextLearningLesson(
  lessons: GuideLessonSummary[],
  currentLessonKey: string,
  search = "",
) {
  const ordered = getLearningPathLessons(lessons, search)
  const index = ordered.findIndex((lesson) => lesson.key === currentLessonKey)

  return index < 0 ? null : (ordered[index + 1] ?? null)
}

/** Home and learning overview share achievable goals, never a prerequisite or access gate. */
export function getLearningPathProgress(lessons: GuideLessonSummary[], search = "") {
  const ordered = getLearningPathLessons(lessons, search)
  const goals = milestones.map((milestone) => {
    const chapters = milestone.lessonKeys.flatMap((key) => {
      const lesson = lessons.find((candidate) => candidate.key === key)

      return lesson ? [lesson] : []
    })
    const completedCount = chapters.filter((lesson) => lesson.completed).length

    return {
      ...milestone,
      completedCount,
      totalCount: milestone.lessonKeys.length,
      completed: completedCount === milestone.lessonKeys.length,
      nextLesson: chapters.find((lesson) => !lesson.completed) ?? null,
    }
  })

  return {
    milestones: goals,
    nextMilestone:
      goals.find(
        (milestone) =>
          (!isLearningSetupPath(search) || milestone.key === "einrichtung") &&
          !milestone.completed &&
          milestone.nextLesson,
      ) ?? null,
    nextLesson: ordered.find((lesson) => !lesson.completed) ?? null,
  }
}
